import _classCallCheck from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/createClass.js";
import { BigNumber } from 'ethers';
/**
 * The `CurrencyValue` class represents a value tied to a currency. The methods include:
 * - `static fromString(currency, value)` - creates a new CurrencyValue from string.
 * - `static zero(currency)` - creates a new CurrencyValue equal to 0.
 * - `toString()` - returns the value of the CurrencyValue as a decimal string with no formatting.
 * - `format(overrideOptions?)` - formats the value according to the currency. The caller can override the formatting options.
 * - `map(fn)` - returns a new CurrencyValue with value transformed by the callback.
 * - `add(other)` - returns a new CurrencyValue with value being the sum of this value and other value. The argument must be a CurrencyValue with the same Currency.
 * - `sub(other)` - returns a new CurrencyValue with value being the difference of this value and other value. The argument must be a CurrencyValue with the same Currency.
 * - `mul(value)` - returns a new CurrencyValue with value multiplied by the argument.
 * - `div(value)` - returns a new CurrencyValue with value divided by the argument.
 * - `mod(value)` - returns a new CurrencyValue with value modulo the argument.
 * - `equals(other)` - performs an equality check on the currencies and the values of both objects.
 * - `lt(other)` - checks if this value is less than the other value. The argument must be a CurrencyValue with the same Currency.
 * - `lte(other)` - checks if this value is less than or equal to the other value. The argument must be a CurrencyValue with the same Currency.
 * - `gt(other)` - checks if this value is greater than the other value. The argument must be a CurrencyValue with the same Currency.
 * - `gte(other)` - checks if this value is greater than or equal to the other value. The argument must be a CurrencyValue with the same Currency.
 * - `isZero()` - returns true if the value is zero.
 *
 * @public
 */
export var CurrencyValue = /*#__PURE__*/function () {
  function CurrencyValue(currency, value) {
    _classCallCheck(this, CurrencyValue);
    this.currency = currency;
    this.value = value;
  }
  _createClass(CurrencyValue, [{
    key: "toString",
    value: function toString() {
      return this.value.toString();
    }
  }, {
    key: "format",
    value: function format() {
      var overrideOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return this.currency.format(this.value.toString(), overrideOptions);
    }
  }, {
    key: "checkCurrency",
    value: function checkCurrency(other) {
      if (this.currency !== other.currency) {
        throw new TypeError("Currency mismatch ".concat(this.currency.ticker, " != ").concat(other.currency.ticker));
      }
    }
  }, {
    key: "map",
    value: function map(fn) {
      return new CurrencyValue(this.currency, fn(this.value));
    }
  }, {
    key: "add",
    value: function add(other) {
      this.checkCurrency(other);
      return this.map(function (x) {
        return x.add(other.value);
      });
    }
  }, {
    key: "sub",
    value: function sub(other) {
      this.checkCurrency(other);
      return this.map(function (x) {
        return x.sub(other.value);
      });
    }
  }, {
    key: "mul",
    value: function mul(value) {
      return this.map(function (x) {
        return x.mul(value);
      });
    }
  }, {
    key: "div",
    value: function div(value) {
      return this.map(function (x) {
        return x.div(value);
      });
    }
  }, {
    key: "mod",
    value: function mod(value) {
      return this.map(function (x) {
        return x.mod(value);
      });
    }
  }, {
    key: "equals",
    value: function equals(other) {
      return this.currency === other.currency && this.value.eq(other.value);
    }
  }, {
    key: "lt",
    value: function lt(other) {
      this.checkCurrency(other);
      return this.value.lt(other.value);
    }
  }, {
    key: "lte",
    value: function lte(other) {
      this.checkCurrency(other);
      return this.value.lte(other.value);
    }
  }, {
    key: "gt",
    value: function gt(other) {
      this.checkCurrency(other);
      return this.value.gt(other.value);
    }
  }, {
    key: "gte",
    value: function gte(other) {
      this.checkCurrency(other);
      return this.value.gte(other.value);
    }
  }, {
    key: "isZero",
    value: function isZero() {
      return this.value.isZero();
    }
  }], [{
    key: "fromString",
    value: function fromString(currency, value) {
      return new CurrencyValue(currency, BigNumber.from(value));
    }
  }, {
    key: "zero",
    value: function zero(currency) {
      return new CurrencyValue(currency, BigNumber.from(0));
    }
  }]);
  return CurrencyValue;
}();